import React, { useState, useEffect } from 'react';
import API_CONFIG from '../config/api';
import { CacheStatus } from '../types/api';
import { requestManager } from '../utils/requestManager';
import { cacheEventManager } from '../utils/cacheEventManager';

const Settings: React.FC = () => {
    const [cacheStatus, setCacheStatus] = useState<CacheStatus | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [refreshing, setRefreshing] = useState<boolean>(false);

    // API Helper Function - bypass cache for Settings to always get fresh data
    const apiCall = async (endpoint: string, method: string = 'GET') => {
        try {
            const url = `${API_CONFIG.BASE_URL}${endpoint}`;

            // For cache-status, always bypass cache to get fresh data
            if (endpoint === API_CONFIG.ENDPOINTS.CACHE.STATUS) {
                // Clear any cached data for cache-status
                requestManager.cancelRequest(url);
                // Make direct fetch to bypass cache
                const response = await fetch(url, { method });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                const data = await response.json();
                if (!data.success) throw new Error(data.message || 'API call failed');
                return data;
            } else {
                // Use request manager for other endpoints
                const data = await requestManager.makeRequest(url, { method });
                if (!data.success) throw new Error(data.message || 'API call failed');
                return data;
            }
        } catch (error) {
            console.error(`API Error for ${endpoint}:`, error);
            throw error;
        }
    };

    const fetchCacheStatus = async () => {
        try {
            setLoading(true);
            const data = await apiCall(API_CONFIG.ENDPOINTS.CACHE.STATUS);
            setCacheStatus(data);
        } catch (error) {
            console.error('Error fetching cache status:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleForceRefresh = async () => {
        try {
            setRefreshing(true);
            const data = await apiCall(API_CONFIG.ENDPOINTS.CACHE.CLEAR, 'POST');
            if (data.success) {
                // Emit cache cleared event to notify other components
                cacheEventManager.emitCacheCleared();
                // Refresh cache status after clearing
                await fetchCacheStatus();
            }
        } catch (error) {
            console.error('Error clearing cache:', error);
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchCacheStatus();

        // Subscribe to cache cleared events from other components
        const unsubscribe = cacheEventManager.subscribe(() => {
            fetchCacheStatus();
        });

        // Cleanup subscription on unmount
        return () => {
            unsubscribe();
        };
    }, []);

    const formatTime = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        if (hours > 0) {
            return `${hours}h ${minutes % 60}m`;
        }
        return `${minutes}m`;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Loading settings...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col min-h-0">
            {/* Content */}
            <main className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
                <div className="max-w-4xl mx-auto space-y-6">
                    {/* Cache Management */}
                    <div className="glass-card p-6">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-6">Cache Management</h3>

                        {cacheStatus ? (
                            <div className="space-y-4">
                                {/* Responsive container for Cache Status and Entries */}
                                <div className="flex flex-col lg:flex-row gap-6 h-full max-h-[67vh]">
                                    {/* Left side: Cache Status */}
                                    <div className="w-full lg:w-1/2 space-y-4">
                                        <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Cache Status</h4>
                                            <div className="flex items-center">
                                                <div className={`w-3 h-3 rounded-full mr-2 ${!cacheStatus.cache_status.cache_empty ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                                <span className="text-sm text-gray-800 dark:text-white">
                                                    {!cacheStatus.cache_status.cache_empty ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Total Entries</h4>
                                            <p className="text-sm text-gray-800 dark:text-white">
                                                {cacheStatus.cache_status.total_entries}
                                            </p>
                                        </div>

                                        <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Memory Usage</h4>
                                            <p className="text-sm text-gray-800 dark:text-white">
                                                {cacheStatus.cache_status.memory_usage_kb} KB
                                            </p>
                                        </div>

                                        <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Cache Duration</h4>
                                            <p className="text-sm text-gray-800 dark:text-white">
                                                {cacheStatus.cache_status.cache_duration_seconds
                                                    ? formatTime(cacheStatus.cache_status.cache_duration_seconds)
                                                    : 'Not configured'
                                                }
                                            </p>
                                        </div>
                                    </div>

                                    {/* Right side: Cache Entries with proper scrolling */}
                                    <div className="w-full lg:w-1/2 flex flex-col min-h-0">
                                        {cacheStatus.cache_status.entries && Object.keys(cacheStatus.cache_status.entries).length > 0 ? (
                                            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg flex-1 flex flex-col min-h-0">
                                                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">Cache Entries</h4>
                                                <div className="space-y-2 overflow-y-auto flex-1 pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 dark:scrollbar-thumb-gray-600 dark:scrollbar-track-gray-800">
                                                    {Object.entries(cacheStatus.cache_status.entries).map(([key, entry]) => (
                                                        <div key={key} className="flex justify-between items-center p-2 glass-card rounded-lg">
                                                            <div>
                                                                <span className="text-sm font-medium text-gray-800 dark:text-white">{key}</span>
                                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                    Created: {new Date(entry.timestamp).toLocaleString()}
                                                                </div>
                                                            </div>
                                                            <div className="text-right">
                                                                <div className="text-sm text-gray-800 dark:text-white">
                                                                    Age: {formatTime(entry.age_seconds)}
                                                                </div>
                                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                    Size: {entry.size_kb} KB
                                                                </div>
                                                                <div className={`text-xs ${entry.is_expired ? 'text-red-500' : 'text-green-500'}`}>
                                                                    {entry.is_expired ? 'Expired' : 'Valid'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-1">
                                                <p className="text-sm text-gray-500 dark:text-gray-400">No cache entries found</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                    <div>
                                        <h4 className="text-sm font-medium text-blue-600 dark:text-blue-400">Force Refresh</h4>
                                        <p className="text-sm text-blue-600 dark:text-blue-400">
                                            Clear cache and fetch fresh data from the database
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleForceRefresh}
                                        disabled={refreshing}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {refreshing ? 'Refreshing...' : 'Force Refresh'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <div className="text-gray-500 dark:text-gray-400 mb-4">
                                    <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <p className="text-gray-500 dark:text-gray-400">Unable to fetch cache status</p>
                            </div>
                        )}
                    </div>

                </div>
            </main>
        </div>
    );
};

export default Settings;



